import React from 'react';

/**
 * Semantic color variant.
 * @typedef {'neutral' | 'info' | 'success' | 'warning' | 'critical' | 'purple'} BadgeTone
 */

const TONE_STYLES = {
  neutral:  { backgroundColor: 'rgba(107,114,128,0.14)', borderColor: 'rgba(107,114,128,0.35)', color: '#d1d5db' },
  info:     { backgroundColor: 'rgba(59,130,246,0.14)',  borderColor: 'rgba(59,130,246,0.40)',  color: '#93c5fd' },
  success:  { backgroundColor: 'rgba(34,197,94,0.14)',   borderColor: 'rgba(34,197,94,0.40)',   color: '#86efac' },
  warning:  { backgroundColor: 'rgba(234,179,8,0.16)',   borderColor: 'rgba(234,179,8,0.40)',   color: '#fde047' },
  critical: { backgroundColor: 'rgba(239,68,68,0.16)',   borderColor: 'rgba(239,68,68,0.45)',   color: '#fca5a5' },
  purple:   { backgroundColor: 'rgba(168,85,247,0.16)',  borderColor: 'rgba(168,85,247,0.40)',  color: '#d8b4fe' },
};

/**
 * One shape, six tones, optional leading dot. Sentence-case labels
 * ("In progress", not "in_progress"). Source of truth: `badges.md` at repo root.
 *
 * @param {object} props
 * @param {BadgeTone} [props.tone] Default `'neutral'`.
 * @param {boolean}   [props.dot]  Render a 6×6 dot in currentColor. Use in dense tables; omit in headers and drawers.
 * @param {string}    [props.className] Forwarded for edge-case spacing.
 * @param {React.ReactNode} props.children
 */
export default function Badge({ tone = 'neutral', dot = false, className = '', children }) {
  const style = TONE_STYLES[tone] || TONE_STYLES.neutral;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border font-medium leading-none whitespace-nowrap ${className}`}
      style={{ ...style, padding: '3px 9px', fontSize: '11.5px' }}
    >
      {dot && (
        <span
          aria-hidden="true"
          style={{ width: 6, height: 6, borderRadius: 9999, backgroundColor: 'currentColor', flex: 'none' }}
        />
      )}
      {children}
    </span>
  );
}
